import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { 
  Link2, MapPin, Clock, Bell, Lock, Sparkles, 
  ArrowRight, Search, Shield, Users, ChevronRight,
  Eye, EyeOff, HelpCircle, Info, Loader2, CheckCircle,
  Scan, Route, UserSearch, Fingerprint, BarChart3, Activity,
  User
} from 'lucide-react';
import { subscribeEmail } from '@/lib/api';
import { trackPaywallHit } from '@/lib/telemetry';
import InvestigativeMatchCard from '@/components/InvestigativeMatchCard';

import NamusImage from '@/components/NamusImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to check if user has pro access
export const hasProAccess = (user) => {
  // First check the user object from context
  if (user) {
    // Admin always has access
    if (user.is_admin) return true;
    
    const plan = user.subscription_plan || 'free';
    
    // Pro subscription
    if (plan === 'pro') return true;
    
    // Active trial
    if (plan === 'trial') {
      const trialEnds = user.trial_ends_at;
      if (trialEnds) {
        const endsAt = new Date(trialEnds);
        if (endsAt > new Date()) return true;
      }
      // Even without trial_ends_at, if plan is trial, grant access
      return true;
    }
    
    // Legacy plans
    if (['professional', 'organization'].includes(plan)) return true;
  }
  
  // Fallback: check localStorage for cached user data
  // This handles cases where auth context hasn't loaded yet
  try {
    const token = localStorage.getItem('token');
    if (token) {
      // If we have a token, the user is logged in
      // Try to get cached user data from session storage
      const cachedUser = sessionStorage.getItem('cached_user');
      if (cachedUser) {
        const userData = JSON.parse(cachedUser);
        if (userData.is_admin) return true;
        if (['pro', 'trial', 'professional', 'organization'].includes(userData.subscription_plan)) return true;
      }
    }
  } catch (e) {
    // Ignore storage errors
  }
  
  return false;
};

// Helper to store upgrade context (match they were trying to view)
const storeUpgradeContext = (matchId) => {
  const context = {
    matchId,
    returnUrl: `/potential-matches${matchId ? `?highlight=${matchId}` : ''}`,
    timestamp: Date.now()
  };
  localStorage.setItem('upgradeContext', JSON.stringify(context));
};

// No Photo Available placeholder component - neutral, no face
const NoPhotoPlaceholder = () => (
  <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-600">
    <User className="w-6 h-6 mb-1 opacity-40" />
    <span className="text-[7px] uppercase tracking-wider font-medium">No Photo</span>
    <span className="text-[6px] uppercase tracking-wider opacity-60">Available</span>
  </div>
);

// Card-sized case photo with NO stock fallbacks - shows placeholder if no real photo
const CardCasePhoto = ({ src }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // If no source URL or error loading, show neutral placeholder (not a stock face)
  if (!src || error) {
    return (
      <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
        <User className="w-5 h-5 mb-0.5 opacity-40" />
        <span className="text-[6px] uppercase tracking-wider font-medium">No Photo</span>
      </div>
    );
  }
  
  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 animate-pulse flex items-center justify-center">
          <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
        </div>
      )}
      <img 
        src={src}
        alt="Case photo"
        className={`w-full h-full object-cover object-center ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{
          filter: 'blur(4px) brightness(0.95)',
          transform: 'scale(1.02)', // Minimal zoom
          transition: 'opacity 0.3s ease'
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {/* Subtle watermark - only on real photos */}
      {loaded && (
        <div className="absolute bottom-0.5 right-0.5 text-[5px] text-white/25 font-medium tracking-wide select-none pointer-events-none">
          LinkTheMissing Analysis
        </div>
      )}
    </>
  );
};

// Modal-sized case photo with blur for non-Pro users
const ModalCasePhoto = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // If no source URL or error loading, show neutral placeholder (not a stock face)
  if (!src || error) {
    return (
      <div className="w-full h-full bg-slate-100 dark:bg-slate-800 flex flex-col items-center justify-center text-slate-400 dark:text-slate-500">
        <User className="w-10 h-10 mb-1 opacity-40" />
        <span className="text-[9px] uppercase tracking-wider font-medium">No Photo</span>
        <span className="text-[8px] uppercase tracking-wider opacity-60">Available</span>
      </div>
    );
  }
  
  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 animate-pulse flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      )}
      <img 
        src={src}
        alt={alt || 'Case photo'}
        loading="lazy"
        className={`w-full h-full object-cover object-center ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ 
          filter: 'blur(6px) brightness(0.95)',
          transform: 'scale(1.05)',
          transition: 'opacity 0.3s ease' 
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {/* Subtle watermark - only on real photos */}
      {loaded && (
        <div className="absolute bottom-1 right-1 text-[6px] text-white/30 font-medium tracking-wide select-none pointer-events-none">
          LinkTheMissing Analysis
        </div>
      )}
    </>
  );
};

// Transform API response to match card format
const transformApiMatch = (apiMatch, index) => ({
  id: apiMatch.id || `match-${index}`,
  match_score: apiMatch.match_score || 70,
  signals: apiMatch.signals || { facial: true, geographic: true, timeline: false, physical: true },
  scores: apiMatch.scores || { facial: 65, location: 60, timeline: 50, physical: 62 },
  // Preserve nested data for InvestigativeMatchCard
  missing_person: apiMatch.missing_person,
  unidentified_person: apiMatch.unidentified_person,
  // Old format for backward compatibility
  case_a_image: apiMatch.missing_person?.photo_url || null,
  case_b_image: apiMatch.unidentified_person?.photo_url || null,
  case_a_meta: {
    age: apiMatch.missing_person?.age || '—',
    location: apiMatch.missing_person?.location || 'Unknown',
    gender: apiMatch.missing_person?.gender
  },
  case_b_meta: {
    age: apiMatch.unidentified_person?.age || '—',
    location: apiMatch.unidentified_person?.location || 'Unknown',
    gender: apiMatch.unidentified_person?.gender
  },
  isRealData: true,
  source: apiMatch.source || 'database'
});

// Case Photo component with loading state and error handling - NO stock fallbacks
const CasePhoto = ({ src, alt, className = '', showWatermark = true }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  
  // If no source URL or error loading, show neutral placeholder (not a face)
  if (!src || error) {
    return <NoPhotoPlaceholder />;
  }
  
  return (
    <>
      {!loaded && (
        <div className="absolute inset-0 bg-slate-100 dark:bg-slate-800 animate-pulse" />
      )}
      <img 
        src={src}
        alt={alt || 'Case photo'}
        className={`w-full h-full object-cover object-center ${className} ${loaded ? 'opacity-100' : 'opacity-0'}`}
        style={{
          filter: 'blur(4px) brightness(0.95)',
          transform: 'scale(1.02)', // Minimal scale to avoid aggressive zoom
          transition: 'opacity 0.3s ease'
        }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
      {/* Subtle watermark overlay - only on real photos */}
      {showWatermark && loaded && (
        <div className="absolute bottom-0.5 right-0.5 text-[5px] text-white/25 font-medium tracking-wide select-none pointer-events-none">
          LinkTheMissing Analysis
        </div>
      )}
    </>
  );
};

// Helper to get confidence tier - forensic language
const getConfidenceTier = (score, activeSignals) => {
  if (score >= 80 && activeSignals >= 4) return { tier: "High", desc: "High cross-signal convergence" };
  if (score >= 75 && activeSignals >= 3) return { tier: "High", desc: "Elevated similarity alignment" };
  if (score >= 65 && activeSignals >= 3) return { tier: "Moderate", desc: "Multi-domain signal overlap" };
  if (score >= 60) return { tier: "Moderate", desc: "Supporting indicators detected" };
  return { tier: "Low", desc: "Limited signal correlation" };
};

// Case File Card - Investigative document style
const AnalyticalMatchCard = ({ match, index, isLast }) => {
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  const activeSignals = Object.values(match.signals).filter(Boolean).length;
  const confidence = getConfidenceTier(match.match_score, activeSignals);
  
  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
      className="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Case File Card - dark investigative theme */}
      <div 
        className={`bg-slate-800/90 border border-slate-700 rounded-sm transition-shadow duration-150 ${
          isHovered ? 'shadow-md shadow-slate-900/50' : 'shadow-sm'
        }`}
      >
        {/* Document Header - tighter spacing */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-700 bg-slate-800/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                Surface Analysis
              </div>
              <div className="text-[10px] text-slate-500">
                Case Preview #{index + 1}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                Confidence Tier
              </div>
              <div className={`text-[11px] font-semibold ${
                confidence.tier === 'High' ? 'text-slate-200' : 
                confidence.tier === 'Moderate' ? 'text-slate-300' : 
                'text-slate-400'
              }`}>
                {confidence.tier}
              </div>
            </div>
          </div>
        </div>
        
        {/* Case Comparison - True 3-column grid layout */}
        <div className="px-4 py-3">
          <div className="grid grid-cols-[120px_1fr_120px] gap-2 items-start">
            {/* LEFT COLUMN: Missing Person */}
            <div className="flex flex-col items-center">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Missing Person
              </div>
              <div className="w-[68px] h-[85px] rounded-sm overflow-hidden bg-slate-700 relative border border-slate-600">
                <CardCasePhoto src={match.case_a_image} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1.5 text-center leading-tight">
                Age {match.case_a_meta?.age || '—'}<br/>
                {match.case_a_meta?.location || 'Unknown'}
              </div>
            </div>
            
            {/* CENTER COLUMN: Similarity Score - perfectly centered */}
            <div className="flex flex-col items-center justify-center pt-1 min-w-[70px] sm:min-w-[85px]">
              <div className="relative w-14 h-14 sm:w-[72px] sm:h-[72px]">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-slate-700"
                    strokeWidth="2.5"
                    stroke="currentColor"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="text-slate-400"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="none"
                    strokeDasharray={`${match.match_score}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-lg sm:text-xl font-bold text-white leading-none tracking-tight">
                    {match.match_score}%
                  </span>
                </div>
              </div>
              <div className="text-[7px] sm:text-[8px] font-semibold text-slate-400 uppercase tracking-wider mt-1.5 whitespace-nowrap">
                Match Score
              </div>
              <div className="text-[8px] sm:text-[9px] text-slate-500 mt-0.5 text-center max-w-[90px] sm:max-w-[100px] leading-tight">
                {confidence.desc}
              </div>
            </div>
            
            {/* RIGHT COLUMN: Unidentified Person */}
            <div className="flex flex-col items-center">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                Unidentified Person
              </div>
              <div className="w-[68px] h-[85px] rounded-sm overflow-hidden bg-slate-700 relative border border-slate-600">
                <CardCasePhoto src={match.case_b_image} />
              </div>
              <div className="text-[10px] text-slate-400 mt-1 text-center leading-tight">
                Age est. {match.case_b_meta?.age || '—'}<br/>
                {match.case_b_meta?.location || 'Unknown'}
              </div>
            </div>
          </div>
        </div>
        
        {/* Contributing Factors - tighter */}
        <div className="px-4 py-2.5 border-t border-slate-700 bg-slate-800/30">
          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
            Contributing Factors ({activeSignals})
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-0">
            <div className={`flex items-center gap-1.5 text-[11px] ${match.signals.facial ? 'text-slate-300' : 'text-slate-500'}`}>
              {match.signals.facial ? (
                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border border-slate-600" />
              )}
              <span>Facial similarity</span>
            </div>
            <div className={`flex items-center gap-1.5 text-[11px] ${match.signals.geographic ? 'text-slate-300' : 'text-slate-500'}`}>
              {match.signals.geographic ? (
                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border border-slate-600" />
              )}
              <span>Geographic proximity</span>
            </div>
            <div className={`flex items-center gap-1.5 text-[11px] ${match.signals.timeline ? 'text-slate-300' : 'text-slate-500'}`}>
              {match.signals.timeline ? (
                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border border-slate-600" />
              )}
              <span>Timeline alignment</span>
            </div>
            <div className={`flex items-center gap-1.5 text-[11px] ${match.signals.physical ? 'text-slate-300' : 'text-slate-500'}`}>
              {match.signals.physical ? (
                <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <span className="w-3 h-3 rounded-full border border-slate-600" />
              )}
              <span>Physical description overlap</span>
            </div>
          </div>
        </div>
        
        {/* Review CTA - tighter */}
        <div className="px-4 py-2.5 border-t border-slate-700">
          <button
            onClick={() => setShowComparisonModal(true)}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-slate-600 hover:bg-slate-700/50 rounded-sm transition-colors text-slate-300"
          >
            <Lock className="w-3 h-3" />
            <span className="text-[11px] font-semibold">Review Detailed Comparison</span>
          </button>
          <p className="text-[8px] text-slate-500 text-center mt-1">
            Side-by-side analysis · Geographic overlay · Timeline review
          </p>
        </div>
      </div>
    </motion.div>
    
    {/* Structural divider between cases - tighter */}
    {!isLast && (
      <div className="my-3 border-t border-slate-300 dark:border-slate-600" />
    )}
    
      {/* Professional Comparison Modal - Redesigned */}
      {showComparisonModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 flex items-center justify-center bg-black/70 z-50 p-4"
          onClick={() => setShowComparisonModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Score secondary, Comparison primary */}
            <div className="p-5 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Overall Similarity
                </div>
                <button 
                  onClick={() => setShowComparisonModal(false)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <EyeOff className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900 dark:text-white">{match.match_score}%</span>
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                Structured Case Comparison
              </div>
            </div>
            
            {/* DOMINANT SECTION: Side-by-Side Case Layout with Real Images */}
            <div className="p-5">
              <div className="flex items-stretch justify-center gap-6 mb-6">
                {/* Case A - Real visible image */}
                <div className="flex-1 max-w-[160px]">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3 relative bg-slate-100 dark:bg-slate-800 shadow-md">
                    <ModalCasePhoto src={match.case_a_image} alt="Missing Person" />
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">Case A</div>
                    <div className="text-[10px] text-slate-400">Missing Person</div>
                  </div>
                </div>
                
                {/* Connection indicator - minimal, not dominant */}
                <div className="flex flex-col items-center justify-center py-6">
                  <div className="text-[10px] text-slate-400 mb-1 uppercase tracking-wide">Similarity</div>
                  <div className="text-base font-bold text-slate-600 dark:text-slate-300">{match.scores?.facial || '—'}%</div>
                </div>
                
                {/* Case B - Real visible image */}
                <div className="flex-1 max-w-[160px]">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3 relative bg-slate-100 dark:bg-slate-800 shadow-md">
                    <ModalCasePhoto src={match.case_b_image} alt="Unidentified Person" />
                  </div>
                  <div className="text-center">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">Case B</div>
                    <div className="text-[10px] text-slate-400">Unidentified Person</div>
                  </div>
                </div>
              </div>
              
              {/* Similarity Signals Detected */}
              <div className="mb-6">
                <div className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wide mb-3">
                  Similarity Signals Detected
                </div>
                <div className="space-y-3">
                  {/* Timeline Alignment */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 dark:text-slate-300">Timeline Alignment</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{match.scores.timeline}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-slate-400 dark:bg-slate-500 rounded-full"
                        style={{ width: `${match.scores.timeline}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Geographic Overlap */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 dark:text-slate-300">Geographic Overlap</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{match.scores.location}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-slate-400 dark:bg-slate-500 rounded-full"
                        style={{ width: `${match.scores.location}%` }}
                      />
                    </div>
                  </div>
                  
                  {/* Facial Similarity */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-600 dark:text-slate-300">Facial Similarity</span>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{match.scores.facial}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-slate-400 dark:bg-slate-500 rounded-full"
                        style={{ width: `${match.scores.facial}%` }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Upgrade #2: Dynamic analytical line */}
                <p className="text-xs text-slate-400 mt-3 text-center">
                  {Object.values(match.signals).filter(Boolean).length} similarity signals detected across cases.
                </p>
              </div>
              
              {/* CTA */}
              <div className="text-center pt-4 border-t border-slate-200 dark:border-slate-700">
                <Link 
                  to="/pricing"
                  onClick={() => storeUpgradeContext(match.id)}
                >
                  <Button className="bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 gap-2 mb-3">
                    <Activity className="w-4 h-4" />
                    Activate Professional Analysis
                  </Button>
                </Link>
                {/* Upgrade #3: Enhanced analytical weight */}
                <p className="text-xs text-slate-500 max-w-xs mx-auto">
                  Professional tools provide structured signal transparency and case comparison tools for responsible evaluation.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

// Main Matches Paywall Component
export default function MatchesPaywall() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPreview, setShowPreview] = useState(true);
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [sampleMatches, setSampleMatches] = useState([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(true);
  const [dataSource, setDataSource] = useState(null);

  // Funnel telemetry — fire paywall_hit once when a non-pro user lands here.
  // hasProAccess returns false for guests + free users; pro users hit the
  // early-return below before this effect.
  useEffect(() => {
    if (hasProAccess(user)) return;
    trackPaywallHit('matches', {
      authenticated: Boolean(isAuthenticated),
      current_plan: user?.subscription_plan || (isAuthenticated ? 'free' : null),
    });
  }, []);

  // Fetch real case matches from the API
  useEffect(() => {
    const fetchSampleMatches = async () => {
      setIsLoadingMatches(true);
      try {
        const response = await fetch(`${API}/public/sample-matches`);
        if (response.ok) {
          const data = await response.json();
          setSampleMatches(data.matches || []);
          setDataSource(data);
        } else {
          setSampleMatches([]);
          setDataSource(null);
        }
      } catch (error) {
        console.error('Failed to fetch sample matches:', error);
        setSampleMatches([]);
        setDataSource(null);
      } finally {
        setIsLoadingMatches(false);
      }
    };
    fetchSampleMatches();
  }, []);

  // If user has pro access, they shouldn't see this
  if (hasProAccess(user)) {
    return null;
  }

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }

    setIsSubscribing(true);
    try {
      const result = await subscribeEmail(email, 'match_alerts');
      setSubscribed(true);
      setEmail('');
      toast({
        title: result.already_subscribed ? "Already subscribed!" : "Successfully subscribed!",
        description: result.already_subscribed 
          ? "You're already on our list. We'll notify you when matches are available."
          : "You'll receive notifications when new matches are found.",
      });
    } catch (error) {
      toast({
        title: "Subscription failed",
        description: error.message || "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Case Connection Analysis
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 max-w-lg mx-auto">
            Our algorithm continuously scores potential connections between missing persons and unidentified remains across 7 national databases.
          </p>
        </motion.div>

        {/* Live Stats from API */}
        {dataSource && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6"
          >
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  {(dataSource.total_cases || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500">cases indexed</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="text-xl sm:text-2xl font-bold text-safety-orange-600">
                  {(dataSource.total_matches || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500">active matches</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="text-xl sm:text-2xl font-bold text-green-600">
                  {(dataSource.moderate_matches || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500">warrant investigation</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="mb-6 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg"
        >
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800 dark:text-amber-200">
              These are algorithmic starting points — not identifications. Verification by qualified investigators is required.
            </p>
          </div>
        </motion.div>

        {/* Top Matches - Compact Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
            Highest-Scoring Connections
          </h2>

          {isLoadingMatches ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
            </div>
          ) : sampleMatches.length > 0 ? (
            <div className="space-y-3">
              {sampleMatches.map((match, index) => {
                const score = match.match_score;
                const tier = match.confidence_tier;
                const tierColor = tier === 'STRONG' 
                  ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800' 
                  : tier === 'MODERATE' 
                    ? 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' 
                    : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
                
                return (
                  <motion.div
                    key={match.id || index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.25 + index * 0.08 }}
                  >
                    <Card className="border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                      <CardContent className="p-4">
                        {/* Score + Tier Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge className={`text-[10px] px-2 py-0.5 font-semibold border ${tierColor}`}>
                              {tier}
                            </Badge>
                            <span className="text-lg font-bold text-slate-900 dark:text-white">
                              {score}%
                            </span>
                          </div>
                          {match.flags && match.flags.length > 0 && (
                            <div className="flex gap-1">
                              {match.flags.map(flag => (
                                <Badge key={flag} variant="outline" className="text-[9px] px-1.5 py-0 bg-red-50 text-red-600 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">
                                  {flag.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* MP vs UID */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div className="flex gap-2.5">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                              {match.missing_person?.photo_url ? (
                                <NamusImage
                                  src={match.missing_person.photo_url}
                                  alt={match.missing_person.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] font-semibold text-safety-orange-600 uppercase mb-0.5">Missing</div>
                              <p className="text-sm font-medium text-slate-900 dark:text-white leading-tight truncate">
                                {match.missing_person?.name}
                              </p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {[match.missing_person?.age, match.missing_person?.location].filter(Boolean).join(' \u00B7 ')}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2.5">
                            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 flex-shrink-0">
                              {match.unidentified_person?.photo_url ? (
                                <NamusImage
                                  src={match.unidentified_person.photo_url}
                                  alt={match.unidentified_person.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <User className="w-5 h-5 text-slate-400" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] font-semibold text-purple-600 uppercase mb-0.5">Unidentified</div>
                              <p className="text-sm font-medium text-slate-900 dark:text-white leading-tight truncate">
                                {match.unidentified_person?.name}
                              </p>
                              <p className="text-[11px] text-slate-500 truncate">
                                {[match.unidentified_person?.age, match.unidentified_person?.location].filter(Boolean).join(' \u00B7 ')}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Signal Pills */}
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {match.signals?.map(signal => (
                            <span key={signal} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
                              {signal}
                            </span>
                          ))}
                        </div>

                        {/* Top reason */}
                        {match.match_reasons?.[0] && (
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic truncate">
                            {match.match_reasons[0]}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <Info className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Match analysis is being processed.</p>
            </div>
          )}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <Card className="border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
            <CardContent className="p-6 text-center">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                Full Analysis Access
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 max-w-md mx-auto">
                View complete match breakdowns, attribute comparisons, investigator notes, and 122 dump site clusters.
              </p>

              <div className="grid grid-cols-2 gap-2 mb-5 max-w-sm mx-auto text-left">
                {[
                  'Full 5-tier signal breakdown',
                  'Tattoo & scar matching',
                  'Timeline & geographic analysis',
                  'Dump site cluster map',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/pricing" onClick={() => storeUpgradeContext(null)}>
                  <Button 
                    size="lg" 
                    className="bg-safety-orange-500 hover:bg-safety-orange-600 text-white px-8 gap-2 rounded-full"
                    data-testid="activate-professional-btn"
                  >
                    <Activity className="w-4 h-4" />
                    Start Investigating
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/browse">
                  <Button variant="outline" className="gap-2 rounded-full">
                    <Search className="w-4 h-4" />
                    Browse Cases
                  </Button>
                </Link>
              </div>

              {!isAuthenticated && (
                <p className="text-xs text-slate-500 text-center mt-3">
                  Already have access? <Link to="/login" className="text-slate-700 dark:text-slate-300 hover:underline font-medium">Sign in</Link>
                </p>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Subscribe */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-6"
        >
          <Card className="bg-slate-100 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700">
            <CardContent className="p-5 text-center">
              <Bell className="w-5 h-5 text-slate-500 mx-auto mb-2" />
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm mb-1">
                Get Updates
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Notified when new cases are added or matches found.
              </p>
              
              {subscribed ? (
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Subscribed</span>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="flex gap-2 max-w-sm mx-auto">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 bg-white dark:bg-slate-900"
                    disabled={isSubscribing}
                  />
                  <Button type="submit" variant="outline" disabled={isSubscribing}>
                    {isSubscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer */}
        <p className="text-[11px] text-slate-400 text-center">
          LinkTheMissing does not make identifications. Connections support investigative review.
        </p>
      </div>
    </div>
  );
}

// Compact lock badge for navigation
export const MatchesLockBadge = () => (
  <Lock className="w-3 h-3 text-slate-400 ml-1" />
);
